import { useEffect, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { L3_PATHS } from 'Deer/constants/routes';
import { ActiveSurveysIntentTable } from 'Deer/intent/Engagement/components/ActiveSurveysIntentTable';
import { SubmittedSurveysIntentTable } from 'Deer/intent/Engagement/components/SubmittedSurveysIntentTable';
import { useCreateNewSurveyForm } from 'Deer/pages/Company/Engagement/Surveys/SurveyCreation/dataHook';
import { stringify } from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';
import CreateExperiencePanel from 'screens/Engagement/CreateExperiencePanel';
import { useEngagementCountData } from 'screens/Engagement/LandingPageV2/hooks/dataHooks';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';
import { checkUserIsPlatformAdmin } from 'utils/helpers/rolesAccess';

import { Plus } from 'assets/assetsV1/iconComponents';
import engagementNoDataImageV1 from 'assets/assetsV1/illustrations/newIllustrations/engagementNoDataImageV1.svg';

import { routes } from 'constants/routes';

import { Button } from 'uiToolkitV1/Button';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { PaperWithShadow } from 'uiToolkitV1/Paper';

const TABLE_TYPES_COMP = {
  ACTIVE_SURVEYS: ActiveSurveysIntentTable,
  SUBMITTED_SURVEYS: SubmittedSurveysIntentTable,
};

const EXPERIENCE_SIDEPANELS = {
  CREATE: 'CREATE',
  LIST: 'LIST',
  SHOW: 'SHOW',
};

const CHIP_ARRAYS = [
  { sectionId: 'ACTIVE_SURVEYS', sectionKeyName: 'Active Surveys', beKey: 'activeCount' },
  { sectionId: 'SUBMITTED_SURVEYS', sectionKeyName: 'Submitted Surveys', beKey: 'submittedCount' },
  // { sectionId: 'EXPERIENCE_SHARED', sectionKeyName: 'Experiences Shared' }
];

const CreateNewSurveyButton = () => {
  const { mutate: createNewSurveyForm } = useCreateNewSurveyForm();

  const createNewSurvey = () => {
    createNewSurveyForm();
  };

  return (
    <Button onClick={createNewSurvey} StartIcon={Plus}>
      Create New Survey
    </Button>
  );
};

const MyEngagementIntent = () => {
  const history = useHistory();
  const [currentSidePanel, setCurrentSidePanel] = useState('SHOW');
  const isUserAdmin = checkUserIsPlatformAdmin();

  const { data, isLoading } = useEngagementCountData();
  const { activeCount, submittedCount } = data ?? {};

  const { search } = useLocation();

  const selectedTableId = new URLSearchParams(search).get('surveyType');

  const getRouteHelper = ({ surveyType }) => {
    const stringifiedSurveyType = stringify({ surveyType });
    return checkNewNavEnabled()
      ? `${L3_PATHS['my-data'].engagement.mySurveys}?${stringifiedSurveyType}`
      : `${routes.engagement.index}?${stringifiedSurveyType}`;
  };

  const openPanel = (panel) => {
    setCurrentSidePanel(panel);
  };

  const closePanel = () => {
    setCurrentSidePanel(null);
  };

  const TableComp = TABLE_TYPES_COMP?.[selectedTableId] ?? (() => null);

  const isPanelOpen = (panel) => currentSidePanel === panel;

  useEffect(() => {
    if (!selectedTableId) {
      history.push(
        getRouteHelper({
          surveyType: CHIP_ARRAYS[0].sectionId,
        })
      );
    }
  }, [history, selectedTableId]);

  if (activeCount === 0 && submittedCount === 0) {
    return (
      <PaperWithShadow px={5} py={6}>
        {isUserAdmin ? (
          <EmptyScreen
            heading="No surveys here!"
            description="You haven't received any surveys, when you do they will appear here."
            illustrationSrc={engagementNoDataImageV1}
            CTAComp={CreateNewSurveyButton}
          />
        ) : (
          <EmptyScreen
            heading="No surveys here!"
            description="You haven't received any surveys, when you do they will appear here."
            illustrationSrc={engagementNoDataImageV1}
          />
        )}
      </PaperWithShadow>
    );
  }

  return (
    <PaperWithShadow p={3}>
      <Box>
        <TableComp selectedTableId={selectedTableId} />
      </Box>
      <CreateExperiencePanel open={isPanelOpen(EXPERIENCE_SIDEPANELS.CREATE)} onCancel={closePanel} />
    </PaperWithShadow>
  );
};

export { MyEngagementIntent };
